import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { get, patch, put, type CaptionsInfo, type CaptionsView, type InputTrack, type StreamKey } from '../api';
import { captionsModel, langName, recentPhrases, translationModel } from './captions';
import { StatusBadge, TXT } from './ui';
import { t } from '../i18n';
import { Icon, Note } from '../ui';
import { byKind, codecName, ixOf } from './tracks';
import { refusalText } from '../billing';
import { useSession } from '../session';

/**
 * Captions of the stream on the Live screen (docs/captions-overlay.md §10.6):
 * the language (off, auto or one the recognition workers know) and the state.
 * Shown only to accounts an admin gave captions. Captions go to YouTube
 * destinations that have Send captions on; the video never waits for them.
 *
 * Translation (CAP-8, docs/captions-translation.md §9): "Translate to" picks
 * the languages the YouTube outputs may choose (each output picks its own in
 * its editor); the translation's state; and the last phrases, in the
 * original or in a translation, as the viewers read them.
 */
export function CaptionsPanel({ streamName, captions, streamLive, tracks }: { streamName?: string; captions?: CaptionsView; streamLive: boolean; tracks?: InputTrack[] }) {
  const qc = useQueryClient();
  const keys = useQuery({ queryKey: ['stream-keys'], queryFn: () => get<StreamKey[]>('/api/stream-keys') });
  const info = useQuery({ queryKey: ['captions-info'], queryFn: () => get<CaptionsInfo>('/api/captions'), staleTime: 60_000 });
  const [saving, setSaving] = useState<string | null>(null);
  const [savingTr, setSavingTr] = useState<string[] | null>(null);
  const [err, setErr] = useState('');
  const [view, setView] = useState('orig');
  const [savingVoice, setSavingVoice] = useState<number | null>(null);

  const key = keys.data?.find((k) => k.stream_name === streamName) ?? keys.data?.find((k) => !k.revoked_at);
  const lang = saving ?? key?.captions_language ?? 'off';
  const langs = info.data?.languages ?? [];
  const options = ['off', 'auto', ...langs, ...(lang !== 'off' && lang !== 'auto' && !langs.includes(lang) ? [lang] : [])];
  // The setting decides off; the API's view tells the rest once captions are on.
  const { user } = useSession();
  const m = captionsModel(lang === 'off' ? undefined : captions ?? { state: 'off', language: lang, reason: streamLive ? undefined : 'stream_offline' }, streamLive,
    (code) => refusalText(code, user));

  const tr = info.data?.translation;
  const menu = savingTr ?? key?.captions_translate ?? [];
  const showTranslate = lang !== 'off' && (!!tr?.available || menu.length > 0);
  const tm = lang === 'off' ? undefined : translationModel(captions?.translation);
  // What the viewer can read: the original, and every language translated now or offered.
  const views = ['orig', ...new Set([...(captions?.translation?.languages ?? []), ...menu])];
  const shownView = views.includes(view) ? view : 'orig';
  const phrases = recentPhrases(captions?.recent, shownView).slice(-3);

  // E1 voice track (design system TrackSelection «Voice track»): the audio track captions listen to — a select
  // after Speaker when the stream has more than one audio track; a warn note while the chosen one is away.
  const audio = byKind(tracks, 'audio');
  const voice = savingVoice ?? key?.voice_audio_track ?? 0;
  const voiceLive = voice === 0 || audio.some((x) => x.index === voice && x.state === 'live');
  const voiceOptions = [...new Set([0, ...audio.map((x) => x.index), voice])].sort((a, b) => a - b);
  const voiceLabel = (i: number) => {
    const tr = audio.find((x) => x.index === i);
    return tr ? `${ixOf('audio', i)} · ${codecName(tr.codec)}` : ixOf('audio', i);
  };
  const saveVoice = async (v: number) => {
    if (!key) return;
    setErr('');
    setSavingVoice(v);
    try {
      await patch(`/api/stream-keys/${key.id}`, { voice_audio_track: v });
      await qc.invalidateQueries({ queryKey: ['stream-keys'] });
    } catch (e) {
      setErr((e as Error).message);
    }
    setSavingVoice(null);
  };

  const save = async (language: string, translate?: string[]) => {
    if (!key) return;
    setErr('');
    try {
      await put(`/api/stream-keys/${key.id}/captions`, translate ? { language, translate } : { language });
      await qc.invalidateQueries({ queryKey: ['stream-keys'] });
    } catch (e) {
      setErr((e as Error).message);
    }
  };
  const change = async (v: string) => {
    setSaving(v);
    await save(v);
    setSaving(null);
  };
  const toggle = async (l: string) => {
    const next = menu.includes(l) ? menu.filter((x) => x !== l) : [...menu, l].sort();
    setSavingTr(next);
    await save(lang, next);
    setSavingTr(null);
  };

  return (
    // the system's captions panel (.sx-captions, CaptionsPanel, CB-19): title in accent, LIVE chips never pulse
    <section aria-label={t('captions.title')} data-testid="captions-panel" className="sx-captions">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:gap-5">
        <div className="flex items-center gap-2.5">
          <span className="ttl">{t('captions.panel')}</span>
          <span data-testid="captions-state" data-state={lang === 'off' ? 'off' : captions?.state ?? 'off'}>
            <StatusBadge color={m.color} border={m.color === 'idle' ? 'dashed' : 'solid'}>{m.label.toUpperCase()}</StatusBadge>
          </span>
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span data-testid="captions-detail" className={`text-[13px] ${m.color === 'fail' || m.color === 'warn' ? TXT[m.color] : 'text-text-soft'}`}>{m.detail}</span>
          {m.meta && lang !== 'off' && <span data-testid="captions-meta" className="font-mono text-xs text-muted">{m.meta}</span>}
          {err && <span role="alert" className="inline-flex items-center gap-1 text-xs text-fail"><Icon name="state-fail" size={12} />{err}</span>}
        </div>
        <label className="flex items-center gap-2">
          <span className="text-xs text-muted">{t('captions.language')}</span>
          <select aria-label={t('captions.langAria')} value={lang} disabled={!key || saving !== null} onChange={(e) => void change(e.target.value)}
            className="sx-select h-[34px] min-w-[150px] font-mono text-xs text-text focus:border-accent focus:shadow-focus focus:outline-none disabled:opacity-40">
            {options.map((o) => (
              <option key={o} value={o}>{o === 'off' ? t('captions.off') : langName(o)}</option>
            ))}
          </select>
        </label>
        {audio.length > 1 && (
          <label className="flex items-center gap-2" data-testid="voice-track">
            <span className="text-xs text-muted">{t('captions.voice')}</span>
            <select aria-label={t('captions.voice')} value={voice} disabled={!key || savingVoice !== null} onChange={(e) => void saveVoice(Number(e.target.value))}
              className="sx-select h-[34px] min-w-[150px] font-mono text-xs text-text focus:border-accent focus:shadow-focus focus:outline-none disabled:opacity-40">
              {voiceOptions.map((i) => <option key={i} value={i}>{voiceLabel(i)}</option>)}
            </select>
          </label>
        )}
      </div>
      {streamLive && !voiceLive && (
        <Note state="warn" data-testid="voice-track-gone">
          <b>{t('captions.voiceGone', { ix: ixOf('audio', voice) })}</b> {t('captions.voiceGoneBody', { to: ixOf('audio', 0) })}
        </Note>
      )}

      {showTranslate && (
        <div data-testid="translate-row" className="flex flex-col gap-2 border-t border-line pt-3 md:flex-row md:items-center md:gap-5">
          <div role="group" aria-label={t('translate.to')} className="flex flex-wrap items-center gap-1.5">
            <span className="mr-1 text-xs text-muted">{t('translate.to')}</span>
            {(tr?.languages ?? menu).map((l) => {
              const on = menu.includes(l);
              const same = lang === l; // the stream is in that language: its outputs get the original
              return (
                <button key={l} type="button" aria-pressed={on} disabled={!key || savingTr !== null || same || (!tr?.available && !on)}
                  title={same ? t('translate.same') : undefined} onClick={() => void toggle(l)}
                  className={`sx-lang min-h-[30px] cursor-pointer disabled:cursor-not-allowed disabled:opacity-40 ${on ? 'border-accent/45 bg-accent/10 text-accent' : ''}`}>
                  {on && <Icon name="check" size={12} />}{l.toUpperCase()}
                </button>
              );
            })}
          </div>
          <div className="flex min-w-0 flex-1 items-center gap-2.5">
            {tm ? (
              <>
                <span data-testid="translate-state" data-state={captions?.translation?.state}>
                  <StatusBadge color={tm.color} border={tm.color === 'idle' ? 'dashed' : 'solid'}>{tm.label.toUpperCase()}</StatusBadge>
                </span>
                <span className="flex min-w-0 flex-col gap-0.5">
                  <span data-testid="translate-detail" className={`text-[13px] ${tm.color === 'fail' || tm.color === 'warn' ? TXT[tm.color] : 'text-text-soft'}`}>{tm.detail}</span>
                  {tm.meta && <span data-testid="translate-meta" className="font-mono text-xs text-muted">{tm.meta}</span>}
                </span>
              </>
            ) : (
              <span data-testid="translate-detail" className="text-[13px] text-muted">{menu.length ? t('translate.pickOnOutput') : t('translate.how')}</span>
            )}
          </div>
        </div>
      )}

      {lang !== 'off' && (captions?.recent || views.length > 1) && (
        <div data-testid="captions-view" className="flex flex-col gap-2 border-t border-line pt-3">
          {views.length > 1 && (
            <div role="tablist" aria-label={t('translate.viewAria')} className="flex flex-wrap gap-1">
              {views.map((v) => (
                <button key={v} type="button" role="tab" aria-selected={shownView === v} onClick={() => setView(v)}
                  className={`min-h-[28px] rounded-md px-2.5 text-xs ${shownView === v ? 'bg-accent/10 text-text shadow-[inset_0_-2px_0_var(--accent)]' : 'text-muted hover:text-text'}`}>
                  {v === 'orig' ? t('translate.original') : langName(v)}
                </button>
              ))}
            </div>
          )}
          <div data-testid="captions-phrases" aria-live="polite" className="line flex min-h-[3.5em] flex-col gap-1 text-text-soft">
            {phrases.length ? phrases.map((p, i) => <span key={`${i}-${p}`} className={i === phrases.length - 1 ? 'text-text' : ''}>{p}</span>)
              : <span className="text-muted">{t('translate.empty')}</span>}
          </div>
        </div>
      )}
    </section>
  );
}
